import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { PageHeader } from "@/components/shared/PageHeader";
import { EmptyState } from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LifeBuoy, ShieldAlert, LogIn } from "lucide-react";
import { toast } from "sonner";

export default function Support() {
  const { user, gotoTenant, refreshTenants } = useAtlas();
  const [tenants, setTenants] = useState([]);
  const [target, setTarget] = useState(null);
  const [reason, setReason] = useState("");

  const load = () => api.get("/support/tenants").then((r) => setTenants(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (user?.is_platform_support) load(); }, [user?.id]);

  if (!user?.is_platform_support) {
    return <EmptyState icon={ShieldAlert} title="Platform support only" description="This area is restricted to Total Operations platform support users." />;
  }

  const start = async () => {
    try {
      const { data } = await api.post("/support/sessions", { tenant_id: target.id, reason });
      toast.success("Support session started — all actions are audited");
      setTarget(null); setReason("");
      await refreshTenants();
      gotoTenant(target.slug || target.id);
    } catch (e) { toast.error(apiError(e)); }
  };

  const end = async (t) => {
    try { await api.post(`/support/sessions/${t.active_session.id}/end`); toast.success("Session ended"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const enter = async (t) => { await refreshTenants(); gotoTenant(t.slug || t.id); };

  return (
    <div>
      <PageHeader title="Support Access" subtitle="Deliberately enter a customer tenant for support. Entry, exit & actions are audited." />
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-3 text-sm text-amber-800">
        <LifeBuoy className="w-5 h-5" /> Support access is never a silent super-admin bypass. A visible banner appears in the tenant and every action generates an audit event.
      </div>
      <div className="space-y-3" data-testid="support-tenants-list">
        {tenants.map((t) => (
          <div key={t.id} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4" data-testid={`support-tenant-${t.id}`}>
            <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center font-heading font-bold">{t.name[0]}</div>
            <div className="flex-1">
              <div className="font-heading font-semibold text-slate-900">{t.name}</div>
              <div className="text-xs text-slate-400">{t.is_member ? "You are a member" : "Not a member"}{t.active_session && " · active support session"}</div>
            </div>
            {t.active_session ? (
              <>
                <Button size="sm" variant="outline" onClick={() => enter(t)} data-testid={`support-enter-${t.id}`}><LogIn className="w-4 h-4 mr-1" /> Enter</Button>
                <Button size="sm" variant="outline" className="border-red-200 text-red-600" onClick={() => end(t)} data-testid={`support-end-${t.id}`}>End</Button>
              </>
            ) : t.is_member ? (
              <span className="text-xs text-slate-400">Access via tenant switcher</span>
            ) : (
              <Button size="sm" onClick={() => setTarget(t)} data-testid={`support-start-${t.id}`}><LifeBuoy className="w-4 h-4 mr-1" /> Start support</Button>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent data-testid="support-start-modal">
          <DialogHeader><DialogTitle className="font-heading">Enter {target?.name} as support</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-500">This will start an audited support session. Provide a reason for the record.</p>
            <div className="space-y-1.5"><Label>Reason</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Investigating customer ticket #1234" data-testid="support-reason-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>Cancel</Button>
            <Button onClick={start} data-testid="support-confirm-start">Start audited session</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
